import { FieldHighlightProps, WalletCardPlatform, WalletField } from './types'

/**
 * Which pass faces each field actually shows up on. Drives the preview's
 * auto-switch: hovering a field that isn't on the face currently shown flips
 * the switcher to one that is — so an admin hovering the hero image (Google
 * only) or the strip (Apple only) always sees where it lands. `icon` has no
 * face — it's the notification / pass-list icon — but the Apple preview shows
 * it below the card, so hovering it switches there.
 */
export const WALLET_FIELD_PLATFORMS: Record<WalletField, WalletCardPlatform[]> =
  {
    brandLine: ['apple', 'google'],
    cardTitle: ['google'],
    terms: ['apple', 'google'],
    backgroundColor: ['apple', 'google', 'app'],
    // Google Wallet has no foreground/label color field — it auto-picks text
    // color — so these only apply to Apple + the in-app card.
    foregroundColor: ['apple', 'app'],
    labelColor: ['apple', 'app'],
    support: ['apple', 'google'],
    showExpiry: ['apple', 'google'],
    logo: ['apple', 'google', 'app'],
    icon: ['apple'],
    strip: ['apple'],
    hero: ['google'],
  }

/**
 * Human labels for each linkable field, shown in the floating tag that appears
 * on a preview region while it (or its form control) is active.
 */
export const WALLET_FIELD_LABELS: Record<WalletField, string> = {
  brandLine: 'Brand line',
  cardTitle: 'Card title',
  terms: 'Back-of-card terms',
  backgroundColor: 'Background color',
  foregroundColor: 'Text color',
  labelColor: 'Label color',
  support: 'Support info',
  showExpiry: 'Expiry row',
  logo: 'Logo',
  icon: 'Icon',
  strip: 'Strip image',
  hero: 'Hero image',
}

/** antd colorPrimary — the highlight accent, kept in one place. */
export const HIGHLIGHT_COLOR = '#1677ff'

/**
 * Two-way link between a form control and the preview region(s) it drives.
 * Pointer/focus on either side writes the shared `activeField`; both sides
 * read `isActive` off it. `onMouseEnter`/`onMouseLeave` are used (not `over`/
 * `out`) precisely because React does not bubble them — so nested preview
 * zones light up the innermost region only, never its container too.
 */
export const useFieldHighlight = (
  field: WalletField,
  { activeField, onActiveFieldChange }: FieldHighlightProps
) => {
  const isActive = activeField === field

  const handlers = {
    onMouseEnter: () => onActiveFieldChange?.(field),
    onMouseLeave: () => onActiveFieldChange?.(null),
    onFocusCapture: () => onActiveFieldChange?.(field),
    onBlurCapture: () => onActiveFieldChange?.(null),
  }

  return { isActive, handlers }
}
